// Copyright 2019 Sorama B.V.

import { BindingEngine, bindingMode, Disposable } from "aurelia-binding";
import { bindable, customElement, TaskQueue } from "aurelia-framework";
import { IWorkflowModuleCatalog } from "catalogs/IWorkflowModuleCatalog";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { IUrlStateService } from "../Interfaces/IUrlStateService";
import { IWorkflowModuleConfig } from "../Interfaces/IWorkflowModuleConfig";
import { IWorkflowModuleController, IWorkflowRouter } from "../Interfaces/IWorkflowRouter";
import { WorkflowProperty, WorkflowPropertyManager } from "../WorkflowProperties/WorkflowPropertyObserverManager";
import { WorkflowUriModule, WorkflowUriProperty } from "../WorkflowProperties/WorkflowPropertyUriConverter";
import { WorkflowModuleController } from "../WorkflowRouter/WorkflowModuleController";

@customElement("sidebar-router")
export class SidebarRouter implements IWorkflowRouter {
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    private currentModule: IWorkflowModuleController;

    private moduleId = -1;

    private disposable: Disposable;
    private subscribers: (() => void)[] = [];

    public static prefix = "sidebar";

    public readonly workflowName: string = "sidebar";

    public get workflowProperties(): WorkflowProperty[] {
        return this.workflowPropertyManager ? this.workflowPropertyManager.workflowProperties : undefined;
    }

    constructor(
        private readonly bindingEngine: BindingEngine,
        private readonly moduleCatalog: IWorkflowModuleCatalog,
        private readonly workflowPropertyManager: WorkflowPropertyManager,
        private readonly urlStateService: IUrlStateService,
        private readonly taskQueue: TaskQueue
    ) {}

    public bind(): Promise<void> {
        this.disposable = this.bindingEngine.propertyObserver(window.location, "search").subscribe(() => {
            this.checkQueryString();
        });

        // Init subscribers
        this.workflowPropertyManager.subscribeWorkflowPropertyChanged((properties: WorkflowUriProperty[]) => {
            this.updateQueryString();
        });

        return this.checkQueryString();
    }

    public unbind(): void {
        if (this.disposable) {
            this.disposable.dispose();
        }

        this.workflowPropertyManager.dispose();
    }

    public addModule(
        workflowModule: WorkflowUriModule,
        position?: number,
        variant?: string
    ): Promise<IWorkflowModuleController> {
        const mod = this.moduleCatalog.getWorkflowModules().find((x) => x.name == workflowModule.name);

        if (!mod || (this.currentModule && mod.name == this.currentModule.config.name)) return;

        const waitPromise = this.currentModule ? this.currentModule.close() : Promise.resolve();
        const newModule = new WorkflowModuleController(
            this,
            mod,
            workflowModule.id != undefined ? workflowModule.id : this.moduleId--,
            variant
        );

        return waitPromise.then(() => {
            this.currentModule = newModule;
            this.updateQueryString();
            this.notifySubscribers();

            return newModule;
        });
    }

    public moveModule(workflowModuleId: number, newPosition: number): void {}

    public closeModule(moduleId: number): void {
        if (this.currentModule.moduleId != moduleId) return;

        this.currentModule = undefined;
        this.updateQueryString();
        this.notifySubscribers();
    }

    public getModule(moduleId: number): IWorkflowModuleController {
        if (this.currentModule && this.currentModule.moduleId == moduleId) {
            return this.currentModule;
        }

        return undefined;
    }

    public getModules(workflowModule?: IWorkflowModuleConfig): IWorkflowModuleController[] {
        if (this.currentModule && (!workflowModule || this.currentModule.config.name == workflowModule.name)) {
            return [this.currentModule];
        }

        return [];
    }

    public moduleTypeExists(workflowModuleType: IWorkflowModuleConfig): boolean {
        return this.getModules().length > 0;
    }

    public getModulePosition(moduleId: number): number {
        return this.getModule(moduleId) ? 0 : undefined;
    }

    public scrollIntoView(moduleId: number): void {
        // There is only one module..
    }

    public subscribeModulesChanged(callback: () => void): Disposable {
        this.subscribers.push(callback);
        return <Disposable>{
            dispose: () => {
                const idx = this.subscribers.findIndex((x) => x == callback);
                if (idx !== -1) this.subscribers.splice(idx, 1);
            },
        };
    }

    public addWorkflowProperties(properties: WorkflowProperty[]): void {}

    private notifySubscribers(): void {
        // WAit until the dom is updated
        this.taskQueue.queueTask(() => {
            for (const subscriber of this.subscribers) {
                subscriber();
            }

            ShellViewManager.sidebarNavigationComplete();
        });
    }

    private updateQueryString(): void {
        this.urlStateService.deletePropertyStates(SidebarRouter.prefix);

        if (this.currentModule) {
            const properties: WorkflowProperty[] = [
                { name: "name", value: this.currentModule.config.name },
                ...this.workflowPropertyManager.workflowProperties,
            ];

            this.urlStateService.updatePropertyState(
                properties.map((x) => {
                    x.prefix = SidebarRouter.prefix;
                    return x;
                })
            );
        }
    }

    private checkQueryString(): Promise<void> {
        const props = this.urlStateService.getPropertyState(SidebarRouter.prefix);

        const idx = props.findIndex((x) => x.name == "name");

        if (idx < 0 && this.currentModule) {
            this.closeModule(this.currentModule.moduleId);
            return;
        } else if (idx < 0) {
            return;
        }

        const moduleName = props.splice(idx, 1)[0];
        return Promise.resolve(this.addModule({ name: moduleName.value } as WorkflowUriModule)).then(() => {
            this.workflowPropertyManager.updateWorkflowProperties(props.filter((x) => x.name != "name"));

            for (const subscriber of this.subscribers) {
                subscriber();
            }
        });
    }
}
